import {Button, Checkbox, Col, Collapse, Divider, Radio, Row, Space} from "antd";
import React, {useEffect, useMemo, useState} from "react";
import {IssueFilterDrawer} from "@components/issues-filter-drawer";
import {fixFilterObjectEmptyArrays, IssueFilterDrawerProps} from "@components/issues-filter-drawer/IssueFilterDrawer";
import useHasPermission from "@hooks/useHasPermission";
import {isEqual} from "underscore";
import {IssueFilters} from "@mwServices/Issue";
import {FilterIcon} from "@components/icons/Icons";
import {formatNumber, formatTitleCase} from "@lib/format";
import {useGetCollectionPublishersQuery, useGetCollectionSeriesFormatsQuery} from "@mwServices/User";
import {SCREEN, useWindowSize} from "@components/window-size-provider";

const { Panel } = Collapse;

const SeriesFiltersContent = ({ filters, setFilters}: IssueFilterDrawerProps) => {
    const showValueRibbon = useHasPermission('est_value_ribbon');
    const publishersQuery = useGetCollectionPublishersQuery({});
    const seriesFormatsQuery = useGetCollectionSeriesFormatsQuery({});

    const publishers = useMemo(() => {
        if(publishersQuery.data) return publishersQuery.data;
        return [];
    }, [publishersQuery]);

    const formats = useMemo(() => {
        if(seriesFormatsQuery.data){
            return seriesFormatsQuery.data.filter(format => format.format !== 'error');
        }
        return [];
    }, [seriesFormatsQuery]);

    return (
        <>
            <Row>
                <Col span={24}>
                    <div className="font-18 font-bold" >Sort by:</div>
                    <br/>

                    <Radio.Group
                        className="pad-left-10"
                        onChange={(event) => setFilters({...filters, sort: event.target.value})}
                        value={filters.sort}
                    >
                        <Space direction="vertical">
                            <Radio value={0} >Title A-Z</Radio>
                            <Radio value={2}># Of Comics (Highest to Lowest)</Radio>
                            <Radio value={4} disabled={!showValueRibbon}>Total FMV (Highest to Lowest)</Radio>
                        </Space>
                    </Radio.Group>
                </Col>
            </Row>
            <Divider />

            <Row>
                <Col span={24}>
                    <div className="font-18 font-bold">Filter by:</div>

                    <Collapse className="filtersAccordion" ghost defaultActiveKey={[1,2]} expandIconPosition={"end"}>
                        <Panel header="Publisher" key={1}>
                            <Space direction="vertical">
                                <Checkbox
                                    checked={!filters.publishers}
                                    onChange={(event) => {
                                        setFilters({...filters, publishers: event.target.checked ? undefined : []});
                                    }}
                                >All</Checkbox>
                                {publishers.map((publisher,i) => (
                                    <Checkbox
                                        key={i}
                                        checked={!filters.publishers || filters.publishers.indexOf(publisher.publisher_id) !== -1}
                                        onChange={(event) => {
                                            if(!filters.publishers && !event.target.checked) {
                                                setFilters({...filters, publishers: publishers.map(item => item.publisher_id).filter(item => item !== publisher.publisher_id)});
                                            }else if(filters.publishers) {
                                                let filtered: any = filters.publishers ? [...filters.publishers] : [];
                                                if (event.target.checked) {
                                                    filtered = [...filtered, publisher.publisher_id]
                                                } else {
                                                    filtered = !!filters.publishers ? filters.publishers.filter(item => item !== publisher.publisher_id) : [];
                                                }
                                                if (filtered.length === publishers.length) filtered = undefined;
                                                setFilters({...filters, publishers: filtered});
                                            }
                                        }}
                                    >
                                        {formatTitleCase(publisher.publisher, 'Unknown Publisher')} ({formatNumber(publisher.total)})
                                    </Checkbox>
                                ))}
                            </Space>
                        </Panel>
                        <Panel header="Series Type" key={2}>
                            <Space direction="vertical">
                                <Checkbox
                                    checked={!filters.formats}
                                    onChange={(event) => {
                                        setFilters({...filters, formats: event.target.checked ? undefined : []})
                                    }}
                                >All</Checkbox>
                                {formats.map((format,i) => (
                                    <Checkbox
                                        key={i}
                                        checked={!filters.formats || filters.formats.indexOf(format.format) !== -1}
                                        onChange={(event) => {
                                            if(!filters.formats && !event.target.checked) {
                                                setFilters({...filters, formats: formats.map(item => item.format).filter(item => item !== format.format)});
                                            }else if(filters.formats) {
                                                let filtered: any = filters.formats ? [...filters.formats] : [];
                                                if (event.target.checked) {
                                                    filtered = [...filtered, format.format]
                                                } else {
                                                    filtered = !!filters.formats ? filters.formats.filter(item => item !== format.format) : [];
                                                }
                                                if (filtered.length === formats.length) filtered = undefined;
                                                setFilters({...filters, formats: filtered});
                                            }
                                        }}
                                    >
                                        {formatTitleCase(format.format, 'Unknown Format')}
                                    </Checkbox>
                                ))}
                            </Space>
                        </Panel>

                    </Collapse>

                </Col>
            </Row>
        </>
    );
};

const SeriesFiltersDrawer = ({ filters, setFilters}) => {
    const windowDimensions = useWindowSize();
    const [showFilters, setShowFilters] = useState<boolean>(false);
    const [newFilters, setNewFilters] = useState<IssueFilters>(filters);

    useEffect(() => {
        if(!isEqual(newFilters, filters)) {
            setNewFilters(filters);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    const onClose = () => {
        setNewFilters(filters);
        setShowFilters(false);
    };

    return (
        <>
            <Button className="greyButton" size={"large"} onClick={() => setShowFilters(true)} >
                {windowDimensions.up(SCREEN.SM) && (<span className="filterButtonText">Filter / Sort</span>)}
                <FilterIcon className="font-12" />
            </Button>

            <IssueFilterDrawer visible={showFilters} onClose={onClose} onSearch={() => {
                setShowFilters(false);

                const fixedFilters = fixFilterObjectEmptyArrays(
                    newFilters,
                    filters,
                    ['publishers', 'formats']);

                if(!isEqual(fixedFilters, filters)) {
                    setFilters(fixedFilters);
                }else if(!isEqual(fixedFilters, newFilters)) {
                    setNewFilters(fixedFilters);
                }
            }}>
                <SeriesFiltersContent filters={newFilters} setFilters={setNewFilters}  />
            </IssueFilterDrawer>
        </>
    )
};

export default SeriesFiltersDrawer;